'use client'

import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import { useLanguage } from '@/contexts/language-context'
import { useTranslation } from '@/hooks/use-translation'
import { useFeatureFlags } from '../contexts/feature-flag-context'
import type { WorkflowItem } from '../types/workflow'
import { formatDateOnly } from '@/utils/format-helpers'

interface ExtendWorkOrderModalProps {
  isOpen: boolean
  onClose: () => void
  item: WorkflowItem | null
  configuration?: any
  onValidateAtsPlacement: (atsSourceId: number, atsPlacementId: number) => Promise<ValidateAtsPlacementResponse>
  onExtendWorkOrder: (fboAssignmentId: number, atsPlacementId: number | null, confirmExtendExistingExtension?: boolean) => Promise<ExtendWorkOrderResponse>
}

interface ValidateAtsPlacementResponse {
  isValid: boolean
  atsPlacementId?: number
  clientCompany?: string
  workerName?: string
  lineOfBusiness?: string
  placementDates?: string
  warnings: string[]
}

interface ExistingExtensionDetails {
  fboId?: number | null
  fboWorkOrderNumber?: string | null
  startDate?: string | null
  endDate?: string | null
}

interface ExtendWorkOrderResponse {
  success: boolean
  errorMessage?: string
  workOrderId?: number
  workOrderVersionId?: number
  assignmentId?: number
  fboWorkOrderNumber?: string
  hasExistingExtension?: boolean
  existingExtension?: ExistingExtensionDetails | null
}

type ModalState = 'initial' | 'validating' | 'validated' | 'submitting' | 'processing' | 'success' | 'error' | 'existing-extension-prompt'

export function ExtendWorkOrderModal({
  isOpen,
  onClose,
  item,
  configuration,
  onValidateAtsPlacement,
  onExtendWorkOrder,
}: ExtendWorkOrderModalProps) {
  const { language } = useLanguage()
  const { t } = useTranslation()
  const { isFeatureEnabled } = useFeatureFlags()
  
  const [modalState, setModalState] = useState<ModalState>('initial')
  const [atsPlacementId, setAtsPlacementId] = useState('')
  const [validationResponse, setValidationResponse] = useState<ValidateAtsPlacementResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [createdWorkOrderId, setCreatedWorkOrderId] = useState<number | null>(null)
  const [existingExtension, setExistingExtension] = useState<ExistingExtensionDetails | null>(null)

  const allowExtensionsUseNewPlacementId = isFeatureEnabled('allowExtensionsUseNewPlacementId')

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setModalState('initial')
      setAtsPlacementId('')
      setValidationResponse(null)
      setErrorMessage(null)
      setSuccessMessage(null)
      setCreatedWorkOrderId(null)
      setExistingExtension(null)
    }
  }, [isOpen])

  const handleValidate = async () => {
    if (!item || !atsPlacementId.trim()) return

    setModalState('validating')
    setErrorMessage(null)

    const atsSourceId = item.atsSourceId || 1
    const placementIdNum = parseInt(atsPlacementId, 10)

    if (isNaN(placementIdNum)) {
      setErrorMessage(t('invalidPlacementId'))
      setModalState('initial')
      return
    }

    try {
      const response = await onValidateAtsPlacement(atsSourceId, placementIdNum)
      
      // Check for mismatches between validated data and current item
      const enhancedWarnings = [...response.warnings]
      
      if (response.isValid) {
        // Compare client: use client.name (from API) or fallback to clientName.
        // clientCompany from ATS may be formatted as "id - name", so extract the name part.
        const currentClientName = (item as any).client?.name ?? item.clientName
        if (response.clientCompany && currentClientName) {
          const placementClientName = response.clientCompany.includes(' - ')
            ? response.clientCompany.split(' - ').slice(1).join(' - ').trim()
            : response.clientCompany.trim()

          if (placementClientName.toLowerCase() !== String(currentClientName).toLowerCase().trim()) {
            enhancedWarnings.push(
              t('clientMismatch')
                .replace('{placementClient}', placementClientName)
                .replace('{currentClient}', String(currentClientName))
            )
          }
        }
        
        // Compare worker name
        const currentWorker = item.workerFullName || item.workerName
        if (response.workerName && currentWorker) {
          if (response.workerName.toLowerCase().trim() !== currentWorker.toLowerCase().trim()) {
            enhancedWarnings.push(
              t('workerMismatch')
                .replace('{placementWorker}', response.workerName)
                .replace('{currentWorker}', currentWorker)
            )
          }
        }
      }
      
      const enhancedResponse = { ...response, warnings: enhancedWarnings }
      setValidationResponse(enhancedResponse)

      if (enhancedResponse.warnings.length > 0 && !enhancedResponse.isValid) {
        setModalState('error')
        setErrorMessage(enhancedResponse.warnings.join(', '))
      } else {
        setModalState('validated')
      }
    } catch (error) {
      setModalState('error')
      setErrorMessage(t('failedValidateAtsPlacement'))
    }
  }

  // Shared post-response routing: routes to success, the existing-extension prompt, or error.
  const applyExtendResponse = (response: ExtendWorkOrderResponse, fallbackSuccessMessage: string) => {
    if (response.success) {
      setSuccessMessage(fallbackSuccessMessage)
      setCreatedWorkOrderId(response.workOrderId ?? null)
      setModalState('success')
    } else if (response.hasExistingExtension) {
      setExistingExtension(response.existingExtension ?? null)
      setModalState('existing-extension-prompt')
    } else {
      setModalState('error')
      setErrorMessage(response.errorMessage || t('failedCreateExtension'))
    }
  }

  const handleSubmit = async () => {
    if (!item) return

    setModalState('submitting')
    setErrorMessage(null)

    try {
      const fboAssignmentId = item.fboAssignmentId || 0
      const placementIdNum = atsPlacementId.trim() ? parseInt(atsPlacementId, 10) : null

      const response = await onExtendWorkOrder(fboAssignmentId, placementIdNum)
      applyExtendResponse(
        response,
        `${t('extensionCreatedSuccessFor')} ${response.fboWorkOrderNumber || ''}`
      )
    } catch (error) {
      setModalState('error')
      setErrorMessage(t('errorExtendingWorkOrder'))
    }
  }

  const handleDirectSubmit = async () => {
    if (!item) return

    setModalState('submitting')
    setErrorMessage(null)

    try {
      const fboAssignmentId = item.fboAssignmentId || 0
      const response = await onExtendWorkOrder(fboAssignmentId, null)
      applyExtendResponse(response, t('extensionCreatedSuccess'))
    } catch (error) {
      setModalState('error')
      setErrorMessage(t('errorExtendingWorkOrder'))
    }
  }

  // Re-submits with confirmExtendExistingExtension=true so the backend skips the blocking check
  // and lets FBO API extend the scheduled extension WO.
  const handleConfirmExtendExisting = async () => {
    if (!item) return

    setModalState('submitting')
    setErrorMessage(null)

    try {
      const fboAssignmentId = item.fboAssignmentId || 0
      const placementIdNum = atsPlacementId.trim() ? parseInt(atsPlacementId, 10) : null
      const response = await onExtendWorkOrder(fboAssignmentId, placementIdNum, true)
      applyExtendResponse(
        response,
        `${t('extensionCreatedSuccessFor')} ${response.fboWorkOrderNumber || ''}`
      )
    } catch (error) {
      setModalState('error')
      setErrorMessage(t('errorExtendingWorkOrder'))
    }
  }

  const renderContent = () => {
    if (modalState === 'processing') {
      return (
        <div className="flex flex-col items-center justify-center py-6 space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-900" />
          <p className="text-center text-sm text-muted-foreground">{t('processingExtension')}</p>
        </div>
      )
    }

    if (modalState === 'success') {
      // Build link to the newly created extension using WorkOrderId from CustomPayload
      const openWorkOrderAction = configuration?.actionConfigurations?.find(
        (action: any) => action.actionCode === 'OpenWorkOrder'
      );
      const linkTemplate = openWorkOrderAction?.link;
      const extensionLink = linkTemplate && createdWorkOrderId
        ? linkTemplate.replace('${FboId}', String(createdWorkOrderId))
        : null;

      return (
        <div className="flex flex-col items-center justify-center py-6 space-y-4">
          <CheckCircle2 className="h-12 w-12 text-green-500" />
          <p className="text-center text-sm text-muted-foreground">{successMessage}</p>
          {extensionLink && (
            <a
              href={extensionLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              {t('viewExtensionInFbo')}
            </a>
          )}
          <p className="text-center text-sm text-muted-foreground">{t('extensionChangesWillAppearSoon')}</p>
        </div>
      )
    }

    if (modalState === 'existing-extension-prompt') {
      // Offer to extend the already-scheduled extension WO instead of blocking with an error.
      const openWorkOrderAction = configuration?.actionConfigurations?.find(
        (action: any) => action.actionCode === 'OpenWorkOrder'
      )
      const linkTemplate = openWorkOrderAction?.link
      const targetFboId = existingExtension?.fboId
      const extensionLink = linkTemplate && targetFboId
        ? linkTemplate.replace('${FboId}', String(targetFboId))
        : null
      const displayText = existingExtension?.fboWorkOrderNumber
        ? `#${existingExtension.fboWorkOrderNumber}`
        : (targetFboId ? `#${targetFboId}` : t('openWorkOrder'))
      const startDateFormatted = existingExtension?.startDate ? formatDateOnly(existingExtension.startDate, language) : null
      const endDateFormatted = existingExtension?.endDate ? formatDateOnly(existingExtension.endDate, language) : null

      return (
        <div className="space-y-4 py-4">
          <Alert className="border-amber-300 bg-amber-50 flex items-start gap-3 [&>svg]:static [&>svg]:translate-y-0 [&>svg+div]:translate-y-0 [&>svg~*]:pl-0">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <AlertDescription className="text-amber-900 text-sm">
              {t('extensionAlreadyScheduledDescription')}
            </AlertDescription>
          </Alert>

          {(extensionLink || existingExtension?.fboWorkOrderNumber || startDateFormatted || endDateFormatted) && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                {extensionLink ? (
                  <a
                    href={extensionLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    {displayText}
                  </a>
                ) : existingExtension?.fboWorkOrderNumber && (
                  <span className="font-medium text-gray-700">{displayText}</span>
                )}
                {startDateFormatted && (
                  <div className="text-gray-700">
                    <span className="font-medium">{t('startDate')}:</span> {startDateFormatted}
                  </div>
                )}
                {endDateFormatted && (
                  <div className="text-gray-700">
                    <span className="font-medium">{t('endDate')}:</span> {endDateFormatted}
                  </div>
                )}
              </div>
            </div>
          )}

          <p className="text-sm text-gray-700">
            {t('extendExistingExtensionQuestion')}
          </p>
        </div>
      )
    }

    if (modalState === 'error') {
      // Get the OpenWorkOrder link from configuration
      const openWorkOrderAction = configuration?.actionConfigurations?.find(
        (action: any) => action.actionCode === 'OpenWorkOrder'
      );
      const linkTemplate = openWorkOrderAction?.link || 'https://flexbackoffice.com/next/workorder/0/${FboId}/0/core/';
      const workOrderLink = linkTemplate.replace('${FboId}', String(item?.fboId || 0));
      const displayText = item?.fboWorkOrderNumber || item?.fboAssignmentId || 'Open Work Order';

      return (
        <div className="space-y-4 py-4">
          <Alert variant="destructive" className="border-red-200 bg-red-50 flex items-center gap-3 [&>svg]:static [&>svg]:translate-y-0 [&>svg+div]:translate-y-0 [&>svg~*]:pl-0">
            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <AlertDescription className="text-red-800 font-medium">
              {errorMessage || t('extensionWorkOrderCreationFailed')}
            </AlertDescription>
          </Alert>
          <div className="text-sm text-gray-700 space-y-3">
            <p>
              {t('createExtensionManually')}
            </p>
            <a
              href={workOrderLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              {displayText}
            </a>
          </div>
        </div>
      )
    }

    if (!allowExtensionsUseNewPlacementId) {
      return (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('areYouSureExtend')}
          </p>
        </div>
      )
    }

    return (
      <div className="space-y-6 py-4 relative">
        {modalState === 'submitting' && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
            <Loader2 className="h-8 w-8 animate-spin text-blue-900" />
          </div>
        )}
        <div className="space-y-3">
          <Label htmlFor="atsPlacementId" className="text-base">
            {t('newPlacementId')}
          </Label>
          <Input
            id="atsPlacementId"
            type="number"
            value={atsPlacementId}
            onChange={(e) => { setAtsPlacementId(e.target.value); setErrorMessage(null) }}
            placeholder={t('enterPlacementId')}
            disabled={modalState === 'validating' || modalState === 'submitting' || modalState === 'validated'}
            className={errorMessage ? 'border-red-400' : ''}
          />
          {errorMessage && modalState === 'initial' && (
            <p className="text-sm text-red-600">{errorMessage}</p>
          )}
        </div>

        {modalState === 'validated' && validationResponse && (
          <div className="space-y-3 p-4 bg-muted rounded-md">
            {validationResponse.isValid && (
              <>
                {validationResponse.clientCompany && (
                  <div className="text-sm">
                    <span className="font-medium">{t('client')}:</span>{' '}
                    {validationResponse.clientCompany}
                  </div>
                )}
                {validationResponse.workerName && (
                  <div className="text-sm">
                    <span className="font-medium">{t('worker')}:</span>{' '}
                    {validationResponse.workerName}
                  </div>
                )}
                {validationResponse.placementDates && (
                  <div className="text-sm">
                    <span className="font-medium">{t('dates')}:</span>{' '}
                    {validationResponse.placementDates}
                  </div>
                )}
              </>
            )}
            
            {validationResponse.warnings.length > 0 && (
              <Alert className="border-amber-300 bg-amber-50">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <div className="space-y-1">
                    {validationResponse.warnings.map((warning, index) => (
                      <div key={index}>{warning}</div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderFooter = () => {
    if (modalState === 'processing') {
      return null
    }

    if (modalState === 'error' || modalState === 'success') {
      return (
        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full">
            {t('close')}
          </Button>
        </DialogFooter>
      )
    }

    if (modalState === 'existing-extension-prompt') {
      return (
        <DialogFooter className="gap-3 pt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full">
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmExtendExisting}
            className="rounded-full bg-blue-900 hover:bg-blue-800 text-white"
          >
            {t('extendExistingExtension')}
          </Button>
        </DialogFooter>
      )
    }

    if (!allowExtensionsUseNewPlacementId) {
      return (
        <DialogFooter className="gap-3 pt-4">
          <Button variant="outline" onClick={onClose} disabled={modalState === 'submitting'} className="rounded-full">
            {t('cancel')}
          </Button>
          <Button onClick={handleDirectSubmit} disabled={modalState === 'submitting'} className="rounded-full bg-blue-900 hover:bg-blue-800 text-white">
            {modalState === 'submitting' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('confirm')}
          </Button>
        </DialogFooter>
      )
    }

    if (modalState === 'initial' || modalState === 'validating' || modalState === 'submitting') {
      return (
        <DialogFooter className="flex-col sm:flex-row gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={modalState === 'validating' || modalState === 'submitting'}
            className="rounded-full"
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleDirectSubmit} 
            disabled={modalState === 'validating' || modalState === 'submitting'}
            className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
          >
            {modalState === 'submitting' && !atsPlacementId.trim() && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('continueWithoutPlacementId')}
          </Button>
          <Button 
            onClick={handleValidate} 
            disabled={!atsPlacementId.trim() || modalState === 'validating' || modalState === 'submitting'}
            className="rounded-full bg-blue-900 hover:bg-blue-800 text-white"
          >
            {modalState === 'validating' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('validateAndExtend')}
          </Button>
        </DialogFooter>
      )
    }

    if (modalState === 'validated') {
      const hasWarnings = validationResponse?.warnings && validationResponse.warnings.length > 0

      return (
        <DialogFooter className="gap-3 pt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full">
            {t('cancel')}
          </Button>
          {hasWarnings ? (
            <Button 
              onClick={handleSubmit} 
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
            >
              {t('continueWithWarning')}
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              className="rounded-full bg-blue-900 hover:bg-blue-800 text-white"
            >
              {t('submit')}
            </Button>
          )}
        </DialogFooter>
      )
    }

    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          {item?.workerFullName && (
            <DialogTitle className="text-blue-900 font-semibold text-base">
              {t('worker')}: {item.workerFullName}
            </DialogTitle>
          )}
        </DialogHeader>
        {modalState !== 'error' && modalState !== 'success' && modalState !== 'processing' && modalState !== 'existing-extension-prompt' && (
          <div className="space-y-1 pt-2">
            <h3 className="font-semibold text-lg">{t('extendWorkOrder')}</h3>
            {allowExtensionsUseNewPlacementId && (
              <p className="text-gray-600 text-sm">{t('extendWorkOrderQuestion')}</p>
            )}
          </div>
        )}
        {(modalState === 'error' || modalState === 'success' || modalState === 'processing' || modalState === 'existing-extension-prompt') && (
          <h3 className="font-semibold text-lg pt-2">{t('extendWorkOrder')}</h3>
        )}
        {renderContent()}
        {renderFooter()}
      </DialogContent>
    </Dialog>
  )
}
